import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Microscope, ScanLine, UsersRound, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHead, EmptyState } from "@/components/common";
import DoctorDirectory from "@/components/DoctorDirectory";
import { PriceTag } from "@/components/PriceTag";
import { api } from "@/lib/api";

const AREAS = [["lab", "Lab reports", "Pathology, microbiology, biochemistry, lab medicine", Microscope], ["imaging", "Imaging reports", "X-ray, CT, MRI, ultrasound – radiology", ScanLine]];

function AreaTabs({ value, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 mb-6">{AREAS.map(([v, l, d, I]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`report-area-${v}`}
        className={`text-left rounded-2xl border p-4 flex gap-3 transition-colors ${value === v ? "border-[#2412B5] bg-[#2412B5]/[0.05]" : "border-[#E2E8ED] hover:border-[#2412B5]/40"}`}>
        <I className="h-5 w-5 text-[#2412B5] mt-0.5" /><span><span className="block font-semibold text-sm">{l}</span><span className="block text-xs muted">{d}</span></span>
      </button>))}</div>
  );
}

export function ReportConsult() {
  const [area, setArea] = useState("lab");
  return (
    <div>
      <PageHead eyebrow="Report consult" title="Get your reports explained by a specialist" sub="Book a video or audio call with a pathologist, microbiologist, biochemist, lab medicine specialist or radiologist. You'll attach the reports while booking." />
      <AreaTabs value={area} onChange={setArea} />
      <DoctorDirectory key={area} scope={{ category: "diagnostician", diag_area: area }} bookPath={(id) => `/patient/book/${id}`} />
    </div>
  );
}

function Pick({ title, params, value, onChange, testId }) {
  const [list, setList] = useState(null);
  useEffect(() => { setList(null); api.get("/doctors", { params }).then((r) => setList(r.data)).catch(() => setList([])); }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="surface p-5" data-testid={testId}>
      <h3 className="font-semibold mb-3">{title}</h3>
      {!list ? <Loader2 className="h-5 w-5 animate-spin text-[#2412B5]" /> : !list.length ? <EmptyState title="No doctors available yet" text="Please check back soon." /> : (
        <div className="grid sm:grid-cols-2 gap-3">{list.map((d) => (
          <button key={d.id} type="button" onClick={() => onChange(d)} data-testid={`${testId}-${d.id}`}
            className={`text-left rounded-xl border p-3 transition-colors ${value?.id === d.id ? "border-[#086B2D] bg-[#086B2D]/5" : "border-[#E2E8ED] hover:border-[#086B2D]/40"}`}>
            <span className="flex items-start justify-between gap-2"><span className="font-medium text-sm">{d.full_name}</span>{value?.id === d.id && <Check className="h-4 w-4 text-[#086B2D]" />}</span>
            <span className="block text-xs muted mt-0.5">{d.specialties.join(", ") || d.qualifications}</span>
            <span className="block mt-1 text-sm"><PriceTag strike={d.strike_fee} offer={d.fee} /></span>
          </button>))}</div>)}
    </section>
  );
}

export function JointConsult() {
  const nav = useNavigate();
  const [area, setArea] = useState("lab");
  const [cl, setCl] = useState(null);
  const [dg, setDg] = useState(null);
  return (
    <div>
      <PageHead eyebrow="Joint consult" title="One call, two specialists" sub="Your clinician and a diagnostician review your reports together on one group video/audio call with chat, agree on the best plan and explain it to you." />
      <div className="space-y-5">
        <Pick title="1. Choose your clinician" params={{ category: "clinician" }} value={cl} onChange={setCl} testId="joint-clinician" />
        <section><h3 className="font-semibold mb-3">2. Which reports need explaining?</h3><AreaTabs value={area} onChange={(v) => { setArea(v); setDg(null); }} /></section>
        <Pick title="3. Choose your diagnostician" params={{ category: "diagnostician", diag_area: area }} value={dg} onChange={setDg} testId="joint-diagnostician" />
        <div className="surface p-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm flex items-center gap-2"><UsersRound className="h-4 w-4 text-[#2412B5]" />{cl && dg ? `${cl.full_name} + ${dg.full_name}` : "Select one clinician and one diagnostician"}</p>
          <Button className="rounded-full" disabled={!cl || !dg} onClick={() => nav(`/patient/book/${cl.id}?co=${dg.id}`)} data-testid="joint-continue-btn">Choose a time</Button>
        </div>
      </div>
    </div>
  );
}
